import type { ChangeEvent } from 'react';
import React, { useState, useEffect, useCallback } from 'react';

import type { FormikProps } from 'formik';
import { Formik, Form } from 'formik';
import { useDebounce } from 'react-use';

import { Typography, DialogActions, FormControlLabel, Radio } from '@mui/material';
import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import type { MultipleUserPickerOption } from 'src/components/ui/MultipleUserPicker';
import { MultipleUserPicker } from 'src/components/ui/MultipleUserPicker';
import TextField from 'src/components/ui/TextField';
import Button from 'src/components/ui/Button';

import { colours } from 'src/constants/colours';
import { breakpoints } from 'src/constants/breakpoints';

import type { ModalProps } from 'src/hooks/useModalOpenState';

import type { User } from 'src/types/__generated__/graphql';
import { useSearchAllUsersWithTotalQuery } from 'src/hooks/__generated__/queries';
import { conversationNameCharactersLimit } from 'src/constants/misc';
import { styledComponent } from 'src/utils/styled';
import Icon from 'src/components/ui/Icon';
import useBreakpoints from 'src/hooks/useBreakpoints';

const StyledDialog = styledComponent(Dialog)<{
    $isUpdateModal: boolean;
}>`
    .MuiDialog-paper {
        position: relative;
        min-height: ${({ $isUpdateModal }) => ($isUpdateModal ? '67%' : '75%')};
    }
    @media screen and (max-width: ${breakpoints.tabletPortrait - 1}px) {
        .MuiDialog-paper {
            height: 100%;
        }
    }
`;

const StyledDialogTitle = styledComponent(DialogTitle)`
    padding: 1.5rem;
    padding-bottom: 0.5rem;
`;

const StyledDialogContent = styledComponent(Box)<{
    $isUpdateModal: boolean;
}>`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: ${({ $isUpdateModal }) => ($isUpdateModal ? '1rem 1.5rem 0 1.5rem' : '0 1.5rem')};
    border-radius: 0.5rem;
`;

const StyledDialogActions = styledComponent(DialogActions)`
    border-top: 0.0625rem solid ${colours.grey.G200};
    padding: 1.5rem;
    box-shadow: 0 0.5rem 2.5rem rgb(19 46 72 / 16%);
    z-index: 10;
`;

const StyledFormControlLabel = styledComponent(FormControlLabel)`
    &.MuiFormControlLabel-root {
        margin-left: 0rem;
        margin-right: 0rem;
    }
`;

StyledDialog.defaultProps = {
    maxWidth: 'xs',
    fullWidth: true,
};

interface FormValues {
    groupConversationName: string;
}

interface Props extends ModalProps {
    onSelection: (selectedUsers: User[], groupConversationName: string) => void;
    actualMembersSendbirdIds?: string[];
}

const defaultLimit = 10;
const CreateOrAddMembersGroupConversationModal: React.FC<Props> = ({
    onSelection,
    open,
    onClose,
    actualMembersSendbirdIds,
}) => {
    const { t } = useTranslation(['common', 'contacts']);
    const { isMobile } = useBreakpoints();

    const [query, setQuery] = useState('');
    const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
    const isUpdateModal = Boolean(actualMembersSendbirdIds);

    const [debouncedQuery, setDebouncedQuery] = useState('');
    useDebounce(() => setDebouncedQuery(query), 500, [query]);

    const { data: searchAllUsersWithTotal, loading: searchAllUsersWithTotalLoading } = useSearchAllUsersWithTotalQuery({
        variables: {
            offset: 0,
            limit: defaultLimit,
            search: debouncedQuery.length > 0 ? debouncedQuery : null,
        },
        skip: !open,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const handleClose = () => {
        setSelectedUsers([]);
        setQuery('');
        onClose();
    };

    const handleSubmit = (values: FormValues) => {
        onSelection(selectedUsers, values.groupConversationName);
        handleClose();
    };

    const handleUserSelected = (_event: ChangeEvent<{ value?: string }>, items?: MultipleUserPickerOption[]) => {
        const newSelectedUsers = items?.filter((item) => item.type === 'user').map((item) => item.value) ?? [];
        setSelectedUsers(newSelectedUsers as User[]);
    };

    const handleSearchChanged = (ev: ChangeEvent<{ value: string }>) => {
        setQuery(ev.target.value);
    };

    const optionCardArea = useCallback(
        (option: MultipleUserPickerOption, selected: boolean) => {
            const alreadyInList = Boolean(
                option.type === 'user' &&
                    actualMembersSendbirdIds?.find((sendbirdUserId) => sendbirdUserId === option.value.sendbirdUserId),
            );

            return <MultipleUserPickerOptionCardRightArea forceSelected={alreadyInList} optionSelected={selected} />;
        },
        [actualMembersSendbirdIds],
    );

    useEffect(() => {
        if (selectedUsers.length === 0) {
            setQuery('');
        }
    }, [selectedUsers]);

    const multipleUserPickerOptions = searchAllUsersWithTotal?.searchAllUsersWithTotal?.results?.map((u) => ({
        type: 'user' as const,
        value: u as User,
    }));

    const showLabel = Boolean(query.length < 1 && selectedUsers.length < 1);
    return (
        <Formik
            initialValues={{
                groupConversationName: '',
            }}
            onSubmit={handleSubmit}
        >
            {({ resetForm, values }: FormikProps<FormValues>) => (
                <Form>
                    <StyledDialog
                        $isUpdateModal={isUpdateModal}
                        open={open}
                        onClose={() => {
                            resetForm();
                            handleClose();
                        }}
                        fullScreen={isMobile}
                        data-target-id="create-or-add-members-group-conversation-modal"
                    >
                        <StyledDialogTitle
                            onClose={() => {
                                resetForm();
                                handleClose();
                            }}
                        >
                            <Box display="flex" flexDirection="row" alignItems="center">
                                <Typography
                                    data-target-id={`${
                                        isUpdateModal ? 'add-members' : 'create'
                                    }-group-conversation-modal`}
                                    variant="h3"
                                >
                                    {isUpdateModal ? t('contacts:addAMember') : t('contacts:groupConversation')}
                                </Typography>
                            </Box>
                        </StyledDialogTitle>
                        <StyledDialogContent $isUpdateModal={isUpdateModal}>
                            {!actualMembersSendbirdIds && (
                                <TextField
                                    data-target-id="group-conversation-name-input"
                                    name="groupConversationName"
                                    label={t('contacts:mandatoryConversationName')}
                                    InputProps={{
                                        inputProps: {
                                            maxLength: conversationNameCharactersLimit,
                                        },
                                    }}
                                    showCountDown
                                />
                            )}
                            <MultipleUserPicker
                                name="users"
                                onChangeTextField={handleSearchChanged}
                                onChangeSelectedUsers={handleUserSelected}
                                options={multipleUserPickerOptions}
                                showLabel={showLabel}
                                loading={searchAllUsersWithTotalLoading}
                                optionCardRightArea={optionCardArea}
                                optionDisabled={(option) =>
                                    Boolean(
                                        option.type === 'user' &&
                                            actualMembersSendbirdIds?.find(
                                                (sendbirdId) => sendbirdId === option.value.sendbirdUserId,
                                            ),
                                    )
                                }
                            />
                        </StyledDialogContent>
                        <StyledDialogActions>
                            <Box display="flex" flexDirection="row" justifyContent="end">
                                <Button
                                    data-target-id="group-conversation-cancel-button"
                                    variant="outlined"
                                    onClick={() => {
                                        resetForm();
                                        handleClose();
                                    }}
                                >
                                    {t('common:cancel')}
                                </Button>
                                <Box ml={0.5}>
                                    <Button
                                        data-target-id="group-conversation-submit-button"
                                        onClick={() => handleSubmit(values)}
                                        disabled={!isUpdateModal && !(values.groupConversationName.length > 0)}
                                    >
                                        {isUpdateModal ? t('common:add') : t('common:create')}
                                    </Button>
                                </Box>
                            </Box>
                        </StyledDialogActions>
                    </StyledDialog>
                </Form>
            )}
        </Formik>
    );
};

export default CreateOrAddMembersGroupConversationModal;

type MultipleUserPickerOptionCardRightAreaProps = {
    forceSelected?: boolean;
    optionSelected: boolean;
};
const MultipleUserPickerOptionCardRightArea: React.FC<MultipleUserPickerOptionCardRightAreaProps> = ({
    forceSelected,
    optionSelected,
}) => (
    <StyledFormControlLabel
        name="user"
        checked={forceSelected ?? optionSelected}
        label=""
        control={
            <Radio
                checkedIcon={<Icon name="CheckMarkBlue" />}
                icon={<Icon name="RadioEmpty" color={colours.blackOpacity.O38} />}
            />
        }
    />
);
